import { css, Global } from "@emotion/react";
import { Box, Icon, Typography } from "@outschool/backpack";
import { faArrowLeft } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { useMutation } from "@outschool/ui-apollo";
import { HeroLayoutVariableWidth } from "@outschool/ui-components-classroom";
import { navigate, useQueryParams } from "@patched/hookrouter";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAnalytics } from "use-analytics";

import { isDevelopment } from "../../Env";
import {
  LearnerQuizCompleteQuizAttemptMutation,
  LearnerQuizCompleteQuizAttemptMutationVariables,
  LearnerQuizUpdateQuizAttemptMutation,
  LearnerQuizUpdateQuizAttemptMutationVariables,
} from "../../generated/graphql";
import Footer from "../../layout/nav/Footer";
import Header from "../../layout/nav/Header";
import { learnerRoutes } from "../../lib/Routes";
import { useRandomDidYouKnowFact } from "./decorativeText";
import { QuestionCard } from "./QuestionCard";
import {
  LearnerQuizCompleteQuizAttempt,
  LearnerQuizUpdateQuizAttempt,
} from "./quizQueries";
import { QuizWriterGeneratingCard } from "./QuizWriterGeneratingCard";
import { QuizWriterMyTopicsDrawer } from "./QuizWriterMyTopicsDrawer";
import { QuizWriterResultsView } from "./QuizWriterResultsView";
import { QuizWriterStartForm } from "./QuizWriterStartForm";
import { QuizQuestion } from "./types";
import { useQuizGeneratorWebsocket } from "./useQuizGeneratorWebsocket";
import { useQuizTopicGeneratorWebsocket } from "./useQuizTopicGeneratorWebsocket";
import { useQuizWriterTopicSuggestions } from "./useQuizWriterTopicSuggestions";

const quizWriterScrollbarGutterStyle = css`
  html {
    scrollbar-gutter: auto;
  }
`;

const DEBUG_ATTEMPT_RESULTS_QUESTION: QuizQuestion = {
  question: "Debug completed quiz attempt",
  options: ["Correct"],
  answerIndex: 0,
};

function readQuizTopicUrlBootstrapFromQueryParams(queryParams: {
  topicUid?: unknown;
  debugAttemptUid?: unknown;
}): { topicUid: string; autoGenerate: boolean } {
  const qpTopicUid =
    typeof queryParams.topicUid === "string" ? queryParams.topicUid.trim() : "";
  const debugAttemptUid =
    typeof queryParams.debugAttemptUid === "string"
      ? queryParams.debugAttemptUid.trim()
      : "";
  if (isDevelopment && debugAttemptUid) {
    return { topicUid: "", autoGenerate: false };
  }
  if (qpTopicUid) {
    return { topicUid: qpTopicUid, autoGenerate: true };
  }
  return { topicUid: "", autoGenerate: false };
}

/**
 * The UI for generating quizzes and taking them.
 *
 * Initially, starts a websocket connection that will generate the quiz content
 * and provide progress updates. When the quiz is ready, the learner can take
 * the quiz and see the results. Once the quiz is complete, it starts another
 * websocket connection that will generate the next set of topic suggestions.
 */
export default function Quiz() {
  const { t } = useTranslation("learnerApp\\Quiz");
  const { track } = useAnalytics();
  const [queryParams] = useQueryParams();
  const autoGenerateRef = useRef(false);
  const debugStartedRef = useRef(false);
  const [topicUid, setTopicUid] = useState(() => {
    const { topicUid, autoGenerate } =
      readQuizTopicUrlBootstrapFromQueryParams(queryParams);
    autoGenerateRef.current = autoGenerate;
    return topicUid;
  });
  // Set when the learner picks a chip on this page; URL-only loads resolve the name from suggestions.
  const [topicNameOverride, setTopicNameOverride] = useState("");
  const {
    topics: topicSuggestions,
    loading: topicsLoading,
    refetch: refetchTopicSuggestions,
  } = useQuizWriterTopicSuggestions();
  const topicDisplayName = useMemo(() => {
    if (topicNameOverride) {
      return topicNameOverride;
    }
    if (!topicUid) {
      return "";
    }
    return topicSuggestions.find(t => t.uid === topicUid)?.name ?? "";
  }, [topicNameOverride, topicSuggestions, topicUid]);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({});
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [revealedByIndex, setRevealedByIndex] = useState<
    Record<number, boolean>
  >({});
  // Whether the learner's choice matched the question's correct answer (for attempt persistence).
  const [answeredCorrectByIndex, setAnsweredCorrectByIndex] = useState<
    Record<number, boolean>
  >({});
  const pickRandomDidYouKnowFact = useRandomDidYouKnowFact(t);
  const [didYouKnowFact, setDidYouKnowFact] = useState<string>(
    pickRandomDidYouKnowFact(),
  );
  const refreshDidYouKnowFact = useCallback(() => {
    setDidYouKnowFact(pickRandomDidYouKnowFact());
  }, [pickRandomDidYouKnowFact]);
  const completionSentRef = useRef(false);
  const resetAttemptCompletion = useCallback(() => {
    completionSentRef.current = false;
  }, []);
  const [myTopicsDrawerOpen, setMyTopicsDrawerOpen] = useState(false);

  const {
    isTopicsGenerating,
    topicsGenerationLogStepCount,
    latestTopicGenUserLogMessage,
    topicsGenError,
    unlockedTopics,
    resetTopicGeneration,
    startTopicGeneratorWebsocket,
    closeTopicGeneratorWebsocket,
  } = useQuizTopicGeneratorWebsocket({
    refetchTopicSuggestions,
    refreshDidYouKnowFact,
  });

  const {
    attemptUid,
    setAttemptUid,
    isQuizGenerating,
    setIsQuizGenerating,
    error,
    setError,
    generationLogStepCount,
    latestUserLogMessage,
    startQuizGeneratorWebsocket,
    closeQuizGeneratorWebsocket,
  } = useQuizGeneratorWebsocket({
    topicUid,
    topicDisplayName,
    topicSuggestions,
    setTopicUid,
    setTopicNameOverride,
    setQuestions,
    setSelectedAnswers,
    setActiveQuestionIndex,
    setRevealedByIndex,
    setAnsweredCorrectByIndex,
    resetTopicGeneration,
    refreshDidYouKnowFact,
    resetAttemptCompletion,
  });

  const [updateQuizAttemptMutation] = useMutation<
    LearnerQuizUpdateQuizAttemptMutation,
    LearnerQuizUpdateQuizAttemptMutationVariables
  >(LearnerQuizUpdateQuizAttempt);

  const [completeQuizAttemptMutation] = useMutation<
    LearnerQuizCompleteQuizAttemptMutation,
    LearnerQuizCompleteQuizAttemptMutationVariables
  >(LearnerQuizCompleteQuizAttempt);

  useEffect(() => {
    const debugQuizAttemptUid =
      typeof queryParams.debugAttemptUid === "string"
        ? queryParams.debugAttemptUid.trim()
        : "";
    if (!isDevelopment || !debugQuizAttemptUid || debugStartedRef.current) {
      return;
    }

    debugStartedRef.current = true;
    closeQuizGeneratorWebsocket();
    closeTopicGeneratorWebsocket();
    setIsQuizGenerating(false);
    setError(null);
    setAttemptUid(debugQuizAttemptUid);
    setQuestions([DEBUG_ATTEMPT_RESULTS_QUESTION]);
    setSelectedAnswers({ 0: DEBUG_ATTEMPT_RESULTS_QUESTION.answerIndex });
    setRevealedByIndex({ 0: true });
    setAnsweredCorrectByIndex({ 0: true });
    setActiveQuestionIndex(1);
    completionSentRef.current = true;
    startTopicGeneratorWebsocket(debugQuizAttemptUid);
  }, [
    closeQuizGeneratorWebsocket,
    closeTopicGeneratorWebsocket,
    queryParams.debugAttemptUid,
    setAttemptUid,
    setError,
    setIsQuizGenerating,
    startTopicGeneratorWebsocket,
  ]);

  useEffect(() => {
    if (autoGenerateRef.current && topicUid && !isQuizGenerating) {
      autoGenerateRef.current = false;
      startQuizGeneratorWebsocket();
    }
  }, [topicUid, isQuizGenerating, startQuizGeneratorWebsocket]);

  const showResults =
    questions.length > 0 && activeQuestionIndex >= questions.length;

  const handleSelectAnswer = (questionIndex: number, optionIndex: number) => {
    if (revealedByIndex[questionIndex]) {
      return;
    }
    const question = questions[questionIndex];
    const answeredCorrectly =
      question !== undefined && optionIndex === question.answerIndex;
    const nextSelected = { ...selectedAnswers, [questionIndex]: optionIndex };
    const nextRevealed = { ...revealedByIndex, [questionIndex]: true };
    const nextAnsweredCorrect = {
      ...answeredCorrectByIndex,
      [questionIndex]: answeredCorrectly,
    };
    setSelectedAnswers(nextSelected);
    setRevealedByIndex(nextRevealed);
    setAnsweredCorrectByIndex(nextAnsweredCorrect);
    if (attemptUid && questions.length > 0) {
      void updateQuizAttemptMutation({
        variables: {
          attemptUid,
          attempt: quizAttemptPatchFromAnswerMaps(
            nextSelected,
            nextAnsweredCorrect,
          ),
        },
      }).catch(error => {
        OsPlatform.captureError(error, {
          component: Component.QuizWriter,
        });
      });
    }
  };

  const currentRevealed = revealedByIndex[activeQuestionIndex] ?? false;

  const correctSoFar = useMemo(() => {
    if (questions.length === 0) {
      return 0;
    }
    let n = 0;
    for (let i = 0; i < activeQuestionIndex; i++) {
      if (selectedAnswers[i] === questions[i].answerIndex) {
        n++;
      }
    }
    if (
      currentRevealed &&
      selectedAnswers[activeQuestionIndex] ===
        questions[activeQuestionIndex].answerIndex
    ) {
      n++;
    }
    return n;
  }, [activeQuestionIndex, currentRevealed, questions, selectedAnswers]);

  const handleNextQuestion = () => {
    if (activeQuestionIndex < questions.length - 1) {
      setActiveQuestionIndex(prev => prev + 1);
    } else {
      setActiveQuestionIndex(questions.length);
      if (attemptUid && !completionSentRef.current) {
        completionSentRef.current = true;
        void completeQuizAttemptMutation({
          variables: { attemptUid },
        })
          .then(result => {
            const uid = result.data?.completeQuizAttempt?.uid ?? attemptUid;
            startTopicGeneratorWebsocket(uid);
          })
          .catch(error => {
            completionSentRef.current = false;
            OsPlatform.captureError(error, {
              component: Component.QuizWriter,
            });
          });
      }
    }
  };

  const score = questions.reduce((acc, q, i) => {
    return acc + (selectedAnswers[i] === q.answerIndex ? 1 : 0);
  }, 0);

  const resetToQuizStartForm = () => {
    track("quiz_write_another_click");
    resetTopicGeneration();
    setTopicUid("");
    setTopicNameOverride("");
    completionSentRef.current = false;
    setAttemptUid(null);
    setSelectedAnswers({});
    setRevealedByIndex({});
    setAnsweredCorrectByIndex({});
    setActiveQuestionIndex(0);
    setQuestions([]);
    setError(null);
  };

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!showResults || questions.length === 0) {
      return;
    }
    track("quiz_results_view", {
      topic: topicDisplayName,
      topicUid,
      score,
      total_questions: questions.length,
    });
  }, [showResults, questions.length, score, topicDisplayName, topicUid, track]);

  return (
    <HeroLayoutVariableWidth
      header={<Header includeBlob />}
      footer={<Footer />}
      sx={{
        backgroundColor:
          questions.length > 0 && !isQuizGenerating && !showResults
            ? "#F4F0FD"
            : "grey.50",
      }}
    >
      <Global styles={quizWriterScrollbarGutterStyle} />
      <Box
        sx={{
          maxWidth: "1050px",
          margin: "0 auto",
          padding: { xs: "16px", sm: "24px" },
          width: "100%",
        }}
      >
        <Box sx={{ marginBottom: "1em" }}>
          <Box
            flex
            onClick={() => navigate(learnerRoutes.playPath())}
            sx={{
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: "primary.main",
              width: "fit-content",
              "&:hover": { opacity: 0.8 },
            }}
          >
            <Icon icon={faArrowLeft} sx={{ fontSize: "16px" }} />
            <Typography variant="subtitle1">{t("Back to Play")}</Typography>
          </Box>
        </Box>
        {questions.length === 0 && !isQuizGenerating ? (
          <QuizWriterStartForm
            topics={topicSuggestions}
            selectedTopicUid={topicUid || null}
            onSelectTopic={(uid, name) => {
              setTopicUid(uid);
              setTopicNameOverride(name);
            }}
            onCreateQuiz={startQuizGeneratorWebsocket}
            isQuizGenerating={isQuizGenerating}
            topicsLoading={topicsLoading}
          />
        ) : null}

        {isQuizGenerating && (
          <QuizWriterGeneratingCard
            topicDisplayName={topicDisplayName}
            generationLogStepCount={generationLogStepCount}
            latestUserLogMessage={latestUserLogMessage}
            didYouKnowFact={didYouKnowFact}
          />
        )}

        {error && (
          <Box
            sx={{
              padding: "24px",
              backgroundColor: "error.50",
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "error.200",
              marginBottom: "24px",
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: "error.main", fontSize: "1.4rem" }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {questions.length > 0 && !isQuizGenerating && !showResults && (
          <QuestionCard
            key={activeQuestionIndex}
            topic={topicDisplayName}
            question={questions[activeQuestionIndex]}
            questionIndex={activeQuestionIndex}
            totalQuestions={questions.length}
            correctSoFar={correctSoFar}
            selectedAnswer={selectedAnswers[activeQuestionIndex]}
            showFeedback={currentRevealed}
            onSelectAnswer={handleSelectAnswer}
            onNextQuestion={handleNextQuestion}
          />
        )}

        {showResults && (
          <QuizWriterResultsView
            score={score}
            questionCount={questions.length}
            topicDisplayName={topicDisplayName}
            unlockedTopics={unlockedTopics}
            isTopicsGenerating={isTopicsGenerating}
            topicsGenerationLogStepCount={topicsGenerationLogStepCount}
            latestTopicGenUserLogMessage={latestTopicGenUserLogMessage}
            topicsGenError={topicsGenError}
            didYouKnowFact={didYouKnowFact}
            onGenerate={startQuizGeneratorWebsocket}
            onResetToQuizStartForm={resetToQuizStartForm}
            onOpenMyTopicsDrawer={() => setMyTopicsDrawerOpen(true)}
          />
        )}
      </Box>
      <QuizWriterMyTopicsDrawer
        key={myTopicsDrawerOpen ? "open" : "closed"}
        open={myTopicsDrawerOpen}
        onClose={() => setMyTopicsDrawerOpen(false)}
        onSelectTopic={(uid, name) => {
          startQuizGeneratorWebsocket({ topicUid: uid, topicName: name });
        }}
        refetchTopicSuggestions={refetchTopicSuggestions}
        isQuizGenerating={isQuizGenerating}
      />
    </HeroLayoutVariableWidth>
  );
}

function quizAttemptPatchFromAnswerMaps(
  selectedAnswers: Record<number, number>,
  answeredCorrectByIndex: Record<number, boolean>,
): LearnerQuizUpdateQuizAttemptMutationVariables["attempt"] {
  return {
    selectedAnswers: Object.keys(selectedAnswers).map(k => {
      const questionIndex = Number(k);
      return {
        questionIndex,
        optionIndex: selectedAnswers[questionIndex]!,
      };
    }),
    answeredCorrectByIndex: Object.keys(answeredCorrectByIndex).map(k => {
      const questionIndex = Number(k);
      return {
        questionIndex,
        correct: answeredCorrectByIndex[questionIndex]!,
      };
    }),
  };
}
